import { createContext, useContext, useState, useEffect, useMemo, type ReactNode } from 'react';
import { APP_NAME } from '@/config/app';

/** One breadcrumb a page can publish when the path-derived label is meaningless. */
export interface PageCrumb {
  label: string;
  path: string;
}

interface PageTitleContextType {
  title: string | null;
  setTitle: (t: string | null) => void;
  crumbs: PageCrumb[] | null;
  setCrumbs: (c: PageCrumb[] | null) => void;
}

const PageTitleContext = createContext<PageTitleContextType | null>(null);

export function PageTitleProvider({ children }: { children: ReactNode }) {
  const [title, setTitle] = useState<string | null>(null);
  const [crumbs, setCrumbs] = useState<PageCrumb[] | null>(null);

  const value = useMemo(() => ({ title, setTitle, crumbs, setCrumbs }), [title, crumbs]);

  return <PageTitleContext.Provider value={value}>{children}</PageTitleContext.Provider>;
}

/** Set the page title. Updates both context (for MobileHeader/Breadcrumbs) and document.title. */
export function usePageTitle(title?: string): void {
  const ctx = useContext(PageTitleContext);
  useEffect(() => {
    if (!ctx) return;
    ctx.setTitle(title ?? null);
    return () => {
      ctx.setTitle(null);
    };
  }, [title, ctx]);

  useEffect(() => {
    if (title) {
      document.title = `${title} | ${APP_NAME}`;
    } else {
      document.title = APP_NAME;
    }
    return () => {
      document.title = APP_NAME;
    };
  }, [title]);
}

/**
 * Publish an explicit breadcrumb trail for the current page, replacing the one
 * `Breadcrumbs` derives from the URL.
 *
 * For routes whose path is mostly ids and connector segments the derived trail
 * degrades badly: `/cycles/<id>/evaluate/<id>` reads "Evaluations > Evaluation >
 * Evaluate > Evaluation", because every id segment falls back to the same page
 * title. Only the page knows the cycle and the person, so it hands them over.
 *
 * Pass `null` (e.g. while the data is still loading) to keep the derived trail.
 * The array is compared by value, so callers can pass a fresh literal on every
 * render without looping.
 */
export function usePageBreadcrumbs(crumbs: PageCrumb[] | null): void {
  const ctx = useContext(PageTitleContext);
  const setCrumbs = ctx?.setCrumbs;
  const serialized = crumbs && crumbs.length > 0 ? JSON.stringify(crumbs) : null;

  useEffect(() => {
    if (!setCrumbs) return;
    setCrumbs(serialized ? (JSON.parse(serialized) as PageCrumb[]) : null);
    return () => {
      setCrumbs(null);
    };
  }, [serialized, setCrumbs]);
}

/** Read the current page title (used by MobileHeader + Breadcrumbs). */
export function usePageTitleValue(): string | null {
  const ctx = useContext(PageTitleContext);
  return ctx?.title ?? null;
}

/** Read the page-supplied breadcrumb trail, if any (used by Breadcrumbs). */
export function usePageBreadcrumbsValue(): PageCrumb[] | null {
  const ctx = useContext(PageTitleContext);
  return ctx?.crumbs ?? null;
}
